import { FC, useMemo } from "react";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { pluralize } from "../../../../utils/text";
import { ICPDistributionChart } from "./ICPDistributionChart";

interface GeographyDistributionItem {
  countryCode: string;
  share: number;
  isSignificant: boolean;
  count: number;
}

interface ICPGeographyChartProps {
  /** Geography distribution data from API */
  data: GeographyDistributionItem[];
  /** Height of the chart container */
  height?: string;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays geography distribution chart with proper country name mapping
 */
export const ICPGeographyChart: FC<ICPGeographyChartProps> = ({
  data,
  height,
  isLoading = false,
}) => {
  const { countries } = useTaxonomyContext();

  const countryNameMap = useMemo(() => {
    const map = {} as Record<string, string>;
    for (const country of countries || []) {
      if (country.alpha2) {
        map[country.alpha2] = country.name || "";
      }
    }
    return map;
  }, [countries]);

  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.share - a.share)
      .slice(0, 10)
      .map((item) => ({
        category: countryNameMap[item.countryCode] || item.countryCode,
        value: item.share,
        count: item.count,
        countText: pluralize("company", item.count),
      }));
  }, [data, countryNameMap]);

  const totalCount = useMemo(() => {
    return data.reduce((sum, item) => sum + item.count, 0);
  }, [data]);

  return (
    <ICPDistributionChart
      title="Geography Distribution"
      data={chartData}
      height={height}
      totalCount={totalCount}
      columnLabelFormat="{valueY}% ({count})"
      columnTooltipFormat="{categoryX}: [bold]{valueY}%[/] ({count} {countText})"
      isLoading={isLoading}
    />
  );
};
